/**
 * FIDDUX PDF Report Renderer
 * Converte schema aplicado em HTML profissional com seções, subseções, tabelas, charts, boxes.
 */

import { marked } from 'marked';
import { applyLanguage } from './pdfReportParser';
import { getReportStyles, FIDDUX_BRANDING } from './pdfReportBranding';

const markdownToHtml = (md) => (md ? marked.parse(md, { gfm: true }) : '');

function cleanTableCell(cell) {
  const esc = (t) => (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const s = String(cell).trim();
  const escaped = esc(s);
  return escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

function renderTable(t, highlightRow) {
  const headers = t._headers || t.headers || [];
  const rows = t._rows || t.rows || [];
  let html = `<div class="fiddux-table-wrapper"><table class="fiddux-table"><thead><tr>`;
  headers.forEach((h) => (html += `<th>${cleanTableCell(h)}</th>`));
  html += `</tr></thead><tbody>`;
  rows.forEach((row, rowIdx) => {
    const firstCell = row[0] || '';
    const isHighlight = highlightRow != null && (
      typeof highlightRow === 'number' ? rowIdx === highlightRow : String(firstCell).toLowerCase().includes(String(highlightRow).toLowerCase())
    );
    const isTotalRow = String(firstCell).includes('**TOTAL**') || String(firstCell).includes('**Avg**');
    html += `<tr class="${isTotalRow ? 'fiddux-table-total' : (isHighlight ? 'highlight-row' : '')}">`;
    row.forEach((cell) => {
      html += `<td>${cleanTableCell(cell)}</td>`;
    });
    html += `</tr>`;
  });
  html += `</tbody></table></div>`;
  return html;
}

function renderHighlightBox(box) {
  const iconMap = { warning: '⚠️', trending_up: '📈', shield: '🛡️', info: 'ℹ️', success: '✅' };
  const icon = (typeof box.icon === 'string' && box.icon.length <= 2) ? box.icon : (iconMap[box.icon] || iconMap[box.type] || '•');
  return `
    <div class="fiddux-highlight-box">
      <div class="box-title">${icon} ${box._title || box.title || ''}</div>
      <div class="box-content">${markdownToHtml(box._content || box.content || '')}</div>
    </div>
  `;
}

function renderSubsection(ss) {
  let html = '<div class="fiddux-subsection-gap">';
  if (ss._title || ss.title) {
    html += `<h3 class="fiddux-h3">${ss._title || ss.title}</h3>`;
  }
  if (ss._content || ss.content) {
    html += `<div class="fiddux-body">${markdownToHtml(ss._content || ss.content)}</div>`;
  }
  const tables = ss._tables || ss.tables || [];
  tables.forEach((t) => {
    html += renderTable(t, t._highlightRow || t.highlight_row);
  });
  if (ss._chart || ss.chart) {
    html += renderChartPlaceholder(ss._chart || ss.chart);
  }
  const boxes = ss._boxes || ss.boxes || [];
  boxes.forEach((b) => {
    html += renderHighlightBox(b);
  });
  html += '</div>';
  return html;
}

function renderChartPlaceholder(chart) {
  const id = 'chart_' + Math.random().toString(36).slice(2);
  let configStr;
  try {
    configStr = encodeURIComponent(JSON.stringify(chart));
  } catch (e) {
    configStr = '';
  }
  return `
    <div class="fiddux-chart-container" data-chart-config="${configStr}">
      <div class="fiddux-chart-title">${chart._title || chart.title || ''}</div>
      <canvas id="${id}" width="600" height="220"></canvas>
    </div>
  `;
}

function renderSection(s, sectionIndex, isFirst, schema = {}) {
  let html = '';
  const needBreak = !isFirst && s.include_page_break === true;
  if (needBreak) {
    html += `<div class="fiddux-footer-spacer"></div><div class="fiddux-page-break"></div>`;
  }
  html += `<div class="fiddux-page fiddux-section">`;

  if (s._title || s.title) {
    html += `<h1 class="fiddux-h1">${s._title || s.title}</h1>`;
  }
  let sectionContent = s._content || s.content || '';
  const meta = schema.report_meta || {};
  const appendixSources = meta.appendix_sources || schema.appendix_sources || [];
  const appendixSourcesPt = meta.appendix_sources_pt || [];
  if (s.id === 'appendix' && appendixSources.length) {
    const sources = appendixSourcesPt.length ? appendixSourcesPt : appendixSources;
    const label = appendixSourcesPt.length ? 'Fontes' : 'Sources';
    sectionContent += '\n\n**' + label + '**\n\n' + sources.map((src) => `- ${src}`).join('\n');
  }
  if (sectionContent) {
    html += `<div class="fiddux-body">${markdownToHtml(sectionContent)}</div>`;
  }

  const subsections = s._subsections || s.subsections || [];
  subsections.forEach((ss) => {
    html += renderSubsection(ss);
  });

  const tables = s._tables || s.tables || [];
  tables.forEach((t) => {
    html += renderTable(t, t._highlightRow || t.highlight_row);
  });

  if (s._chart || s.chart) {
    html += renderChartPlaceholder(s._chart || s.chart);
  }

  const boxes = s._boxes || s.boxes || s.highlight_boxes || [];
  boxes.forEach((b) => {
    html += renderHighlightBox(b);
  });

  html += `</div>`;
  return html;
}

export function buildHtmlFromSchema(schema, lang, logoSrc = null) {
  const applied = typeof schema._activeLang !== 'undefined' ? schema : applyLanguage(schema, lang);
  const meta = schema.report_meta || {};

  const logoSrcFinal = logoSrc || null;
  let html = getReportStyles();
  html += `<div class="fiddux-pdf-root">`;

  const esc = (t) => (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const nl2br = (s) => (s || '').split('\n').map((l) => esc(l.trim())).filter(Boolean).join('<br/>');
  const coverSubtitle = (lang === 'pt' ? meta.cover_subtitle_pt : meta.cover_subtitle) || meta.cover_subtitle || '';
  const preparedBy = (lang === 'pt' ? meta.prepared_by_pt : meta.prepared_by) || meta.prepared_by || '';
  const exclusivePartners = (lang === 'pt' ? meta.exclusive_partners_pt : meta.exclusive_partners) || meta.exclusive_partners || '';
  const dateStr = meta.date || new Date().toISOString().split('T')[0];
  const coverDateRaw = (lang === 'pt' ? meta.cover_date_pt : null) || meta.cover_date || (dateStr + ' | Private & Confidential | Internal Use Only');
  const coverDate = coverDateRaw.replace(/\{date\}/g, dateStr);
  const logoOffset = meta.coverLogoOffset ?? -150;
  const titleOffset = meta.coverTitleOffset ?? 200;
  const detailsOffset = meta.coverDetailsOffset ?? 88;
  const dateOffset = meta.coverDateOffset ?? 220;

  html += `
    <div class="fiddux-cover">
      ${logoSrcFinal ? `<img src="${logoSrcFinal}" alt="FIDDUX" class="fiddux-cover-logo" style="margin-top:${logoOffset}px" />` : `<div class="fiddux-cover-logo-text" style="margin-top:${logoOffset}px">FIDDUX</div>`}
      <div class="fiddux-cover-block">
        <h1 class="fiddux-cover-title" style="margin-top:${titleOffset}px">${nl2br(lang === 'pt' ? (meta.title_pt || meta.title) : (meta.title || meta.title_pt))}</h1>
        <div class="fiddux-cover-details" style="margin-top:${detailsOffset}px">
          <p class="fiddux-cover-subtitle">${nl2br(coverSubtitle)}</p>
          <p class="fiddux-cover-prepared">${nl2br(preparedBy)}</p>
          <p class="fiddux-cover-partners">${nl2br(exclusivePartners)}</p>
          <p class="fiddux-cover-date" style="margin-top:${dateOffset}px">${nl2br(coverDate)}</p>
        </div>
      </div>
    </div>
  `;

  const sections = (applied.sections || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  sections.forEach((s, idx) => {
    html += renderSection(s, idx, idx === 0, schema);
  });

  html += `<div class="fiddux-footer-spacer"></div></div>`;
  return html;
}
